import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiResponse,
  PaginationResponse,
  LoginRequest,
  LoginResponse,
  User,
  Booking,
  BookingFormData,
  Companion,
  Bus,
  Trip,
  TripFormData,
  Accommodation,
  Invoice,
  Branch,
  SubscriptionPlan,
  CreateSubscriptionPlanData,
  UpdateSubscriptionPlanData
} from '../types';
import { getApiUrl } from '../config/environment';

// Define the actual response structure from backend
interface ActualLoginResponse {
  success: boolean;
  message: string;
  token: string;
  user: {
    id: string;
    username: string;
    fullName: string;
    role: string;
    branch: {
      id: string;
      name: string;
      code: string;
    };
    permissions: any[];
    lastLogin: string;
    settings: {
      language: string;
      theme: string;
      notifications: {
        email: boolean;
        sms: boolean;
        whatsapp: boolean;
      };
    };
  };
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and vendor headers
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      let currentVendor: { vendorId?: string; vendorName?: string } = {};
      try {
        currentVendor = JSON.parse(localStorage.getItem('currentVendor') || '{}');
      } catch {
        currentVendor = {};
      }

      // Always attach vendor context when available (needed for vendor login before token exists)
      if (currentVendor.vendorId && !config.headers['X-Vendor-ID']) {
        config.headers['X-Vendor-ID'] = currentVendor.vendorId;
        if (currentVendor.vendorName) {
          config.headers['X-Vendor-Name'] = Buffer.from(currentVendor.vendorName, 'utf8').toString('base64');
        }
      }

      if (token && token.trim() !== '') {
        // Validate token format (basic check)
        if (token.split('.').length === 3) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          // Remove invalid token (keep vendor selection so login can retry)
          console.log('🚨 Invalid token format detected, clearing auth data:', {
            tokenLength: token?.length,
            tokenParts: token?.split('.').length,
            currentPath: window.location.pathname
          });
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      return config;
    });

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.log('🔍 API Error Interceptor:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.response?.data?.message
        });

        if (error.response?.status === 401) {
          const currentPath = window.location.pathname;
          const isLoginPage =
            currentPath.includes('/login') || currentPath.includes('/vendor-login');
          const isAuthLoginRequest = String(error.config?.url || '').includes('/auth/login');

          console.log('🚨 401 Unauthorized - API Response:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            message: error.response?.data?.message,
            currentPath
          });

          // Failed login attempts should not wipe vendor selection
          if (!isAuthLoginRequest) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (!isLoginPage) {
              localStorage.removeItem('currentVendor');
              console.log('🔄 Redirecting to login page from 401 error');
              window.location.href = '/login';
            }
          }
        }
        
        // Handle 403 Forbidden (insufficient permissions)
        if (error.response?.status === 403) {
          console.log('🚫 403 Forbidden - Insufficient permissions');
          // Could show a toast notification here
        }
        
        // Handle 500 Server errors
        if (error.response?.status >= 500) {
          console.log('💥 Server error - Could show maintenance message');
        }
        
        // Enhance error with better message extraction
        if (error.response?.data?.message) {
          error.message = error.response.data.message;
        } else if (error.message) {
          // Keep original error message if no API message
          console.log('📝 Using original error message:', error.message);
        } else {
          // Fallback error message
          error.message = 'حدث خطأ غير متوقع';
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async login(credentials: LoginRequest): Promise<ActualLoginResponse> {
    // Use admin endpoint for admin login
    const response: AxiosResponse<ActualLoginResponse> = await this.api.post('/admin/auth/login', credentials);
    return response.data;
  }

  async vendorLogin(
    credentials: LoginRequest & { vendorId?: string }
  ): Promise<ActualLoginResponse> {
    const headers: Record<string, string> = {};
    if (credentials.vendorId) {
      headers['X-Vendor-ID'] = credentials.vendorId;
    }
    const response: AxiosResponse<ActualLoginResponse> = await this.api.post(
      '/vendor/auth/login',
      credentials,
      { headers }
    );
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors
      console.warn('Logout API call failed:', error);
    }
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.get('/auth/me');
    if (!response.data.data) {
      throw new Error('User data not found');
    }
    return response.data.data;
  }

  // User methods
  async getUsers(
    page = 1,
    limit = 10,
    filters: { search?: string; role?: string; isActive?: string } | string = {}
  ): Promise<PaginationResponse<User>> {
    const params: Record<string, string | number> = { page, limit };
    if (typeof filters === 'string') {
      if (filters) params.search = filters;
    } else {
      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      if (filters.isActive === 'true' || filters.isActive === 'false') {
        params.isActive = filters.isActive;
      }
    }
    const response: AxiosResponse<PaginationResponse<User>> = await this.api.get('/users', {
      params,
    });
    return response.data;
  }

  async createUser(userData: Partial<User> & { password?: string }): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.post('/users', userData);
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to create user');
    }
    return response.data.data;
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.put(`/users/${id}`, userData);
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to update user');
    }
    return response.data.data;
  }

  async deleteUser(id: string): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }

  async toggleUserStatus(id: string): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.post(
      `/users/${id}/toggle-status`
    );
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to toggle user status');
    }
    return response.data.data;
  }

  async resetUserPassword(id: string, newPassword: string): Promise<void> {
    const response: AxiosResponse<ApiResponse<unknown>> = await this.api.post(
      `/users/${id}/reset-password`,
      { newPassword }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reset password');
    }
  }

  // Booking methods
  async getBookings(page = 1, limit = 20, filters: any = {}): Promise<PaginationResponse<Booking>> {
    const params: any = { page, limit };
    
    // Add filter parameters
    if (filters.status) params.status = filters.status;
    if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
    if (filters.tripType) params.tripType = filters.tripType;
    if (filters.search) params.search = filters.search;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.tripId) params.tripId = filters.tripId;
    if (filters.busId) params.busId = filters.busId;

    const response: AxiosResponse<PaginationResponse<Booking>> = await this.api.get('/bookings', {
      params
    });
    return response.data;
  }

  async getRecentBookings(limit = 5): Promise<Booking[]> {
    const response: AxiosResponse<PaginationResponse<Booking>> = await this.api.get('/bookings', {
      params: { page: 1, limit, sort: '-createdAt' }
    });
    return response.data.data || [];
  }

  async getBooking(id: string): Promise<Booking> {
    const response: AxiosResponse<ApiResponse<Booking>> = await this.api.get(`/bookings/${id}`);
    if (!response.data.data) {
      throw new Error('Booking not found');
    }
    return response.data.data;
  }

  async createBooking(bookingData: BookingFormData): Promise<Booking> {
    // Transform data to match backend expectations
    const transformedData = {
      ...bookingData,
      // Ensure companions is always an array (even if empty)
      companions: bookingData.companions || []
    };
    
    const response: AxiosResponse<ApiResponse<Booking>> = await this.api.post('/bookings', transformedData);
    if (!response.data.data) {
      throw new Error('Failed to create booking');
    }
    return response.data.data;
  }

  async updateBooking(id: string, bookingData: Partial<BookingFormData>): Promise<Booking> {
    const response: AxiosResponse<ApiResponse<Booking>> = await this.api.put(`/bookings/${id}`, bookingData);
    if (!response.data.data) {
      throw new Error('Failed to update booking');
    }
    return response.data.data;
  }

  async deleteBooking(id: string): Promise<void> {
    await this.api.delete(`/bookings/${id}`);
  }

  /** Convert all UUID boarding tokens to short 5-char codes */
  async upgradeBoardingCodes(): Promise<{ upgraded: number; total: number; message: string }> {
    const response: AxiosResponse<ApiResponse<{ upgraded: number; total: number }>> =
      await this.api.post('/bookings/upgrade-boarding-codes');
    return {
      upgraded: response.data.data?.upgraded || 0,
      total: response.data.data?.total || 0,
      message: response.data.message || '',
    };
  }

  async addCompanion(bookingId: string, companionData: Companion): Promise<Booking> {
    const response: AxiosResponse<ApiResponse<Booking>> = await this.api.post(`/bookings/${bookingId}/companions`, companionData);
    if (!response.data.data) {
      throw new Error('Failed to add companion');
    }
    return response.data.data;
  }

  async removeCompanion(bookingId: string, companionId: string): Promise<Booking> {
    const response: AxiosResponse<ApiResponse<Booking>> = await this.api.delete(`/bookings/${bookingId}/companions/${companionId}`);
    if (!response.data.data) {
      throw new Error('Failed to remove companion');
    }
    return response.data.data;
  }

  // Bus methods
  async getBuses(page = 1, limit = 100, filters: any = {}): Promise<PaginationResponse<Bus>> {
    const params: any = { page, limit };
    
    // Add filter parameters
    if (filters.status) params.status = filters.status;
    if (filters.isActive !== undefined) params.isActive = filters.isActive;
    if (filters.search) params.search = filters.search;

    const response: AxiosResponse<PaginationResponse<Bus>> = await this.api.get('/buses', {
      params
    });
    return response.data;
  }

  async getAvailableBuses(date?: string): Promise<Bus[]> {
    const params: any = {};
    if (date) params.date = date;
    
    const response: AxiosResponse<ApiResponse<Bus[]>> = await this.api.get('/buses/available', {
      params
    });
    return response.data.data || [];
  }

  async getBus(id: string): Promise<Bus> {
    const response: AxiosResponse<ApiResponse<Bus>> = await this.api.get(`/buses/${id}`);
    if (!response.data.data) {
      throw new Error('Bus not found');
    }
    return response.data.data;
  }

  async createBus(busData: Partial<Bus>): Promise<Bus> {
    const response: AxiosResponse<ApiResponse<Bus>> = await this.api.post('/buses', busData);
    if (!response.data.data) {
      throw new Error('Failed to create bus');
    }
    return response.data.data;
  }

  async updateBus(id: string, busData: Partial<Bus>): Promise<Bus> {
    const response: AxiosResponse<ApiResponse<Bus>> = await this.api.put(`/buses/${id}`, busData);
    if (!response.data.data) {
      throw new Error('Failed to update bus');
    }
    return response.data.data;
  }

  async deleteBus(id: string): Promise<void> {
    await this.api.delete(`/buses/${id}`);
  }

  /** Notify bus driver via WhatsApp (includes next trip if assigned) */
  async notifyBusDriver(busId: string): Promise<{ message: string; data?: any }> {
    const response = await this.api.post(`/buses/${busId}/notify-driver`);
    return {
      message: response.data.message,
      data: response.data.data,
    };
  }

  async updateBusStatus(id: string, status: string, notes?: string): Promise<Bus> {
    const response: AxiosResponse<ApiResponse<Bus>> = await this.api.put(`/buses/${id}/status`, {
      status,
      notes
    });
    if (!response.data.data) {
      throw new Error('Failed to update bus status');
    }
    return response.data.data;
  }

  async addMaintenanceRecord(busId: string, maintenanceData: {
    type: 'routine' | 'repair' | 'inspection';
    description: string;
    cost?: number;
    performedBy?: string;
    notes?: string;
  }): Promise<Bus> {
    const response: AxiosResponse<ApiResponse<Bus>> = await this.api.post(`/buses/${busId}/maintenance`, maintenanceData);
    if (!response.data.data) {
      throw new Error('Failed to add maintenance record');
    }
    return response.data.data;
  }

  async getBusMaintenanceHistory(id: string): Promise<{
    maintenance: any;
    maintenanceStatus: string;
    driverLicenseStatus: string;
  }> {
    const response: AxiosResponse<ApiResponse<{
      maintenance: any;
      maintenanceStatus: string;
      driverLicenseStatus: string;
    }>> = await this.api.get(`/buses/${id}/maintenance`);
    if (!response.data.data) {
      throw new Error('Failed to get maintenance history');
    }
    return response.data.data;
  }

  async getMaintenanceDueBuses(): Promise<Bus[]> {
    const response: AxiosResponse<ApiResponse<Bus[]>> = await this.api.get('/buses/maintenance/due');
    return response.data.data || [];
  }

  // Trip methods
  async getTrips(page = 1, limit = 20, filters: any = {}): Promise<PaginationResponse<Trip>> {
    const params: any = { page, limit };
    
    // Add filter parameters
    if (filters.status) params.status = filters.status;
    if (filters.tripType) params.tripType = filters.tripType;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.search) params.search = filters.search;
    if (filters.boardingMine) params.boardingMine = '1';

    const response: AxiosResponse<PaginationResponse<Trip>> = await this.api.get('/trips', {
      params
    });
    return response.data;
  }

  async getTrip(id: string): Promise<Trip> {
    const response: AxiosResponse<ApiResponse<Trip>> = await this.api.get(`/trips/${id}`);
    if (!response.data.data) {
      throw new Error('Trip not found');
    }
    return response.data.data;
  }

  async createTrip(tripData: any): Promise<Trip> {
    const response: AxiosResponse<ApiResponse<Trip>> = await this.api.post('/trips', tripData);
    if (!response.data.data) {
      throw new Error('Failed to create trip');
    }
    return response.data.data;
  }

  async updateTrip(id: string, tripData: any): Promise<Trip> {
    const response: AxiosResponse<ApiResponse<Trip>> = await this.api.put(`/trips/${id}`, tripData);
    if (!response.data.data) {
      throw new Error('Failed to update trip');
    }
    return response.data.data;
  }

  async deleteTrip(id: string): Promise<void> {
    await this.api.delete(`/trips/${id}`);
  }

  /** Notify trip driver(s) via WhatsApp */
  async notifyTripDriver(
    tripId: string,
    busIndex?: number
  ): Promise<{ message: string; data?: any }> {
    const response = await this.api.post(`/trips/${tripId}/notify-driver`, {
      ...(busIndex !== undefined ? { busIndex } : {}),
    });
    return {
      message: response.data.message,
      data: response.data.data,
    };
  }

  // Accommodation methods
  async getAccommodations(page = 1, limit = 20, filters: any = {}): Promise<PaginationResponse<Accommodation>> {
    const params: any = { page, limit };
    
    // Add filter parameters
    if (filters.city) params.city = filters.city;
    if (filters.search) params.search = filters.search;
    if (filters.isActive !== undefined) params.isActive = filters.isActive;

    const response: AxiosResponse<PaginationResponse<Accommodation>> = await this.api.get('/accommodations', {
      params
    });
    return response.data;
  }

  /** Active accommodations for booking dropdowns (no operations permission required) */
  async getAccommodationsForBooking(): Promise<Accommodation[]> {
    const response: AxiosResponse<ApiResponse<Accommodation[]>> = await this.api.get('/dashboard/accommodations');
    return response.data.data || [];
  }

  /** Users eligible for boarding staff assignment on manual bookings */
  async getBoardingStaffForBooking(): Promise<User[]> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get('/dashboard/boarding-staff');
    return response.data.data || [];
  }

  async getAccommodation(id: string): Promise<Accommodation> {
    const response: AxiosResponse<ApiResponse<Accommodation>> = await this.api.get(`/accommodations/${id}`);
    if (!response.data.data) {
      throw new Error('Accommodation not found');
    }
    return response.data.data;
  }

  async createAccommodation(accommodationData: Partial<Accommodation>): Promise<Accommodation> {
    const response: AxiosResponse<ApiResponse<Accommodation>> = await this.api.post('/accommodations', accommodationData);
    if (!response.data.data) {
      throw new Error('Failed to create accommodation');
    }
    return response.data.data;
  }

  async updateAccommodation(id: string, accommodationData: Partial<Accommodation>): Promise<Accommodation> {
    const response: AxiosResponse<ApiResponse<Accommodation>> = await this.api.put(`/accommodations/${id}`, accommodationData);
    if (!response.data.data) {
      throw new Error('Failed to update accommodation');
    }
    return response.data.data;
  }

  async deleteAccommodation(id: string): Promise<void> {
    await this.api.delete(`/accommodations/${id}`);
  }

  async reserveRooms(accommodationId: string, reservationData: {
    roomsCount: number;
    checkIn: string;
    checkOut: string;
    guestName: string;
    guestPhone?: string;
    notes?: string;
  }): Promise<Accommodation> {
    const response: AxiosResponse<ApiResponse<Accommodation>> = await this.api.post(`/accommodations/${accommodationId}/reserve`, reservationData);
    if (!response.data.data) {
      throw new Error('Failed to reserve rooms');
    }
    return response.data.data;
  }

  // Invoice methods
  async getInvoices(page = 1, limit = 10): Promise<PaginationResponse<Invoice>> {
    const response: AxiosResponse<PaginationResponse<Invoice>> = await this.api.get('/invoices', {
      params: { page, limit }
    });
    return response.data;
  }

  async generateInvoice(bookingId: string): Promise<Invoice> {
    const response: AxiosResponse<ApiResponse<Invoice>> = await this.api.post(`/invoices/generate/${bookingId}`);
    if (!response.data.data) {
      throw new Error('Failed to generate invoice');
    }
    return response.data.data;
  }

  // Branch methods
  async getBranches(): Promise<Branch[]> {
    const response: AxiosResponse<ApiResponse<Branch[]>> = await this.api.get('/branches');
    return response.data.data || [];
  }

  // Dashboard methods
  async getDashboardStats(): Promise<{
    today: { bookings: number; revenue: number };
    month: { bookings: number; revenue: number };
    pending: { bookings: number };
    active: { trips: number };
  }> {
    const response: AxiosResponse<ApiResponse<{
      today: { bookings: number; revenue: number };
      month: { bookings: number; revenue: number };
      pending: { bookings: number };
      active: { trips: number };
    }>> = await this.api.get('/reports/dashboard');
    return response.data.data || {
      today: { bookings: 0, revenue: 0 },
      month: { bookings: 0, revenue: 0 },
      pending: { bookings: 0 },
      active: { trips: 0 }
    };
  }

  // Cash Closing methods
  async getTodayCashClosing(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/reports/cash-closing/today');
    return response.data.data || null;
  }

  async closeCash(data: { closingCash: number; notes?: string }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/reports/cash-closing/close', data);
    return response.data.data;
  }

  async reopenCash(data: { reason: string }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/reports/cash-closing/reopen', data);
    return response.data.data;
  }

  async getCashClosingHistory(params: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<{ data: any[]; pagination: any }> {
    const response: AxiosResponse<any> = await this.api.get('/reports/cash-closing/history', { params });
    // The backend returns data directly as an array, not nested in a data property
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || {}
    };
  }

  // Reports methods
  async getReports(type: string, startDate: string, endDate: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/reports', {
      params: { type, startDate, endDate }
    });
    return response.data.data || {};
  }

  async getReportsOverview(params: {
    startDate?: string;
    endDate?: string;
  } = {}): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/reports/overview', { params });
    return response.data.data;
  }

  async getCustomerReports(params: {
    startDate?: string;
    endDate?: string;
    limit?: number;
  } = {}): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/reports/customers', { params });
    return response.data.data;
  }

  async getTripReports(params: {
    startDate?: string;
    endDate?: string;
  } = {}): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/reports/trips', { params });
    return response.data.data;
  }

  async getBookingAnalytics(params: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  } = {}): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/reports/bookings', { params });
    return response.data.data;
  }

  async getFinancialReports(startDate: string, endDate: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/reports/financial', {
      params: { startDate, endDate }
    });
    return response.data.data;
  }

  // Vendor management methods
  async getVendors(page = 1, limit = 10, search = ''): Promise<PaginationResponse<any>> {
    const response: AxiosResponse<PaginationResponse<any>> = await this.api.get('/vendors', {
      params: { page, limit, search }
    });
    return response.data;
  }

  async registerVendor(vendorData: {
    vendor: {
      name: string;
      companyName: string;
      vendorId?: string;
      contact: {
        phone: string;
        mobile?: string;
        email: string;
        website?: string;
      };
      address: {
        street?: string;
        district?: string;
        city: string;
        postalCode?: string;
        country: string;
      };
      license: {
        commercialRegister: string;
        issuer: string;
        issueDate: string;
        expiryDate: string;
      };
      subscription: {
        plan: 'basic' | 'premium' | 'enterprise';
        endDate: string;
      };
    };
    adminUser: {
      username: string;
      fullName: string;
      password: string;
      email: string;
      phone: string;
    };
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/vendors/register', vendorData);
    return response.data;
  }

  async getPublicVendors(page = 1, limit = 100): Promise<PaginationResponse<any>> {
    // Create a separate axios instance for public calls (no auth header)
    const publicApi = axios.create({
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    const response: AxiosResponse<PaginationResponse<any>> = await publicApi.get('/vendors/public', {
      params: { page, limit }
    });
    return response.data;
  }

  private getPublicApiBaseUrl() {
    return getApiUrl();
  }

  private createPublicBookingClient(vendorId: string) {
    return axios.create({
      baseURL: this.getPublicApiBaseUrl(),
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        'X-Vendor-ID': vendorId,
      },
      params: { vendorId },
    });
  }

  async getPublicBookingOffice(vendorId: string): Promise<{
    vendorId: string;
    name: string;
    companyName: string;
    logo?: string | null;
    contact?: { phone?: string; email?: string };
  }> {
    const client = this.createPublicBookingClient(vendorId);
    const response = await client.get('/public/booking/office');
    return response.data.data;
  }

  async getPublicBookingTrips(
    vendorId: string,
    page = 1,
    limit = 30
  ): Promise<{ data: any[]; pagination?: any }> {
    const client = this.createPublicBookingClient(vendorId);
    const response = await client.get('/public/booking/trips', {
      params: { page, limit, vendorId },
    });
    return {
      data: response.data.data || [],
      pagination: response.data.pagination,
    };
  }

  async getPublicBookingTrip(vendorId: string, tripId: string): Promise<any> {
    const client = this.createPublicBookingClient(vendorId);
    const response = await client.get(`/public/booking/trips/${tripId}`, {
      params: { vendorId },
    });
    return response.data.data;
  }

  async createPublicBooking(
    vendorId: string,
    payload: {
      tripId: string;
      busId?: string;
      contactPerson: Record<string, any>;
      companions?: Record<string, any>[];
      specialRequests?: string;
    }
  ): Promise<{
    bookingNumber: string;
    status: string;
    tripNumber?: string;
    contactName?: string;
  }> {
    const client = this.createPublicBookingClient(vendorId);
    const response = await client.post('/public/booking', { ...payload, vendorId });
    if (!response.data.success) {
      throw new Error(response.data.message || 'فشل الحجز');
    }
    return response.data.data;
  }

  async getPublicInvoice(
    vendorId: string,
    bookingNumber: string
  ): Promise<{
    booking: Booking;
    vendor: {
      vendorId: string;
      name?: string;
      companyName?: string;
      logo?: string | null;
      license?: Record<string, string>;
      contact?: {
        phone?: string;
        mobile?: string;
        email?: string;
        address?: Record<string, string>;
      };
    };
  }> {
    const client = this.createPublicBookingClient(vendorId);
    const response = await client.get(
      `/public/invoice/${encodeURIComponent(bookingNumber)}`,
      {
        params: { vendorId },
      }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'الفاتورة غير موجودة');
    }
    return response.data.data;
  }

  async getVendor(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/vendors/${id}`);
    if (!response.data.data) {
      throw new Error('Vendor not found');
    }
    return response.data.data;
  }

  async createVendor(vendorData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/vendors', vendorData);
    if (!response.data.data) {
      throw new Error('Failed to create vendor');
    }
    return response.data.data;
  }

  async updateVendor(id: string, vendorData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/vendors/${id}`, vendorData);
    if (!response.data.data) {
      throw new Error('Failed to update vendor');
    }
    return response.data.data;
  }

  async deleteVendor(id: string): Promise<void> {
    await this.api.delete(`/vendors/${id}`);
  }

  async reactivateVendor(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/vendors/${id}/reactivate`);
    if (!response.data.data) {
      throw new Error('Failed to reactivate vendor');
    }
    return response.data.data;
  }

  async getVendorStats(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/vendors/stats/overview');
    return response.data.data;
  }

  // Subscription Plans
  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const response: AxiosResponse<SubscriptionPlan[]> = await this.api.get('/subscription-plans');
    return response.data || [];
  }

  async getSubscriptionPlansAdmin(): Promise<SubscriptionPlan[]> {
    const response: AxiosResponse<SubscriptionPlan[]> = await this.api.get('/subscription-plans/admin');
    return response.data || [];
  }

  async createSubscriptionPlan(data: CreateSubscriptionPlanData): Promise<SubscriptionPlan> {
    const response: AxiosResponse<SubscriptionPlan> = await this.api.post('/subscription-plans', data);
    return response.data;
  }

  async updateSubscriptionPlan(id: string, data: UpdateSubscriptionPlanData): Promise<SubscriptionPlan> {
    const response: AxiosResponse<SubscriptionPlan> = await this.api.put(`/subscription-plans/${id}`, data);
    return response.data;
  }

  async deleteSubscriptionPlan(id: string): Promise<void> {
    await this.api.delete(`/subscription-plans/${id}`);
  }

  async toggleSubscriptionPlan(id: string): Promise<SubscriptionPlan> {
    const response: AxiosResponse<SubscriptionPlan> = await this.api.patch(`/subscription-plans/${id}/toggle`);
    return response.data;
  }

  // Vendor Profile and License Management
  async getVendorProfile(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/vendor/profile');
    return response.data.data;
  }

  async updateVendorLicense(licenseData: {
    commercialRegister: string;
    tourismLicense: string;
    transportationLicense: string;
    taxNumber: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/vendor/profile/license', licenseData);
    return response.data.data;
  }

  async updateVendorWhatsApp(data: {
    tenantId: string;
    apiKey?: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      '/vendor/profile/whatsapp',
      data
    );
    return response.data.data;
  }

  async getWhatsAppTemplates(): Promise<{
    templates: Record<string, string>;
    defaults: Record<string, string>;
    meta: Record<string, { label: string; group: string; hint: string }>;
    placeholders: Array<{ key: string; desc: string }>;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      '/vendor/profile/whatsapp-templates'
    );
    return response.data.data;
  }

  async updateWhatsAppTemplates(
    templates: Record<string, string>
  ): Promise<{
    templates: Record<string, string>;
    defaults: Record<string, string>;
    meta: Record<string, { label: string; group: string; hint: string }>;
    placeholders: Array<{ key: string; desc: string }>;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      '/vendor/profile/whatsapp-templates',
      { templates }
    );
    return response.data.data;
  }

  /** Send booking WhatsApp: confirmation | invoice | reminder | payment_pending */
  async sendBookingWhatsApp(
    bookingId: string,
    type: 'confirmation' | 'invoice' | 'reminder' | 'payment_pending' = 'confirmation'
  ): Promise<{ message: string; data?: any }> {
    const path =
      type === 'invoice'
        ? '/whatsapp-notifications/send-booking-invoice'
        : type === 'reminder'
          ? '/whatsapp-notifications/send-booking-reminder'
          : type === 'payment_pending'
            ? '/whatsapp-notifications/send-booking-payment-pending'
            : '/whatsapp-notifications/send-booking-notification';
    const response = await this.api.post(path, { bookingId, type });
    return {
      message: response.data.message,
      data: response.data.data,
    };
  }

  async uploadVendorLogo(logoFile: File): Promise<any> {
    const formData = new FormData();
    formData.append('logo', logoFile);
    
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/vendor/profile/logo', formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data.data;
  }

  // —— Boarding / QR check-in ——
  async getBoardingBuses(tripId: string): Promise<{
    tripId: string;
    tripNumber: string;
    status: string;
    buses: import('../types').BoardingBusSummary[];
  }> {
    const response = await this.api.get(`/boarding/trips/${tripId}/buses`);
    return response.data.data;
  }

  async boardingLookup(
    token: string,
    tripId: string,
    busId?: string | null
  ): Promise<import('../types').BoardingLookupResult> {
    const response = await this.api.get('/boarding/lookup', {
      params: { token, tripId, ...(busId ? { busId } : {}) },
    });
    return response.data.data;
  }

  async boardingCheckIn(
    token: string,
    tripId: string,
    busId?: string | null
  ): Promise<any> {
    const response = await this.api.post('/boarding/check-in', {
      token,
      tripId,
      ...(busId ? { busId } : {}),
    });
    return response.data;
  }

  async getBoardingManifest(
    tripId: string,
    busId?: string | null
  ): Promise<import('../types').BoardingManifest> {
    const response = await this.api.get('/boarding/manifest', {
      params: { tripId, ...(busId ? { busId } : {}) },
    });
    return response.data.data;
  }

  // —— Hotel housing ——
  async housingLookup(token: string): Promise<import('../types').HousingLookupResult> {
    const response = await this.api.get('/housing/lookup', {
      params: { token },
    });
    return response.data.data;
  }

  async getHousingBookings(
    page = 1,
    limit = 20,
    filters: { search?: string; city?: string; complete?: string } = {}
  ): Promise<{
    data: any[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const params: Record<string, any> = { page, limit };
    if (filters.search) params.search = filters.search;
    if (filters.city) params.city = filters.city;
    if (filters.complete) params.complete = filters.complete;
    const response = await this.api.get('/housing', { params });
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  }

  async getHousingBooking(bookingId: string): Promise<any> {
    const response = await this.api.get(`/housing/${bookingId}`);
    return response.data.data;
  }

  async updateHousing(
    bookingId: string,
    payload: {
      housing?: {
        locationText?: string;
        mapsUrl?: string;
        hotelName?: string;
      };
      contactRoomNumber?: string;
      companionRooms?: Array<{
        companionId?: string;
        index?: number;
        roomNumber: string;
      }>;
    }
  ): Promise<any> {
    const response = await this.api.put(`/housing/${bookingId}`, payload);
    return response.data;
  }

  async getHousingMessagePreview(bookingId: string): Promise<{
    message: string;
    housingComplete: boolean;
  }> {
    const response = await this.api.get(
      `/housing/${bookingId}/message-preview`
    );
    return response.data.data;
  }

  async notifyHousing(
    bookingId: string,
    recipients: Array<'contact' | 'office' | 'rep'>,
    message?: string
  ): Promise<any> {
    const response = await this.api.post(`/housing/${bookingId}/notify`, {
      recipients,
      ...(message?.trim() ? { message: message.trim() } : {}),
    });
    return response.data;
  }

  async getHousingReportPreview(
    tripId: string,
    busId?: string
  ): Promise<{
    message: string;
    tripNumber: string;
    date?: string;
    departure?: { date?: string; time?: string; day?: string };
    companyName?: string;
    bus?: {
      busId?: string;
      busNumber?: string;
      driverName?: string;
      driverPhone?: string;
    };
    roomsCount: number;
    bedsCount: number;
    bookingsCount: number;
    rooms: Array<{
      roomNumber: string;
      hotelName?: string;
      beds: Array<{
        name: string;
        phone?: string;
        bookingNumber?: string;
        organization?: string;
      }>;
    }>;
  }> {
    const params: Record<string, string> = { tripId };
    if (busId) params.busId = busId;
    const response = await this.api.get('/housing/report/preview', { params });
    return response.data.data;
  }

  async sendHousingReport(payload: {
    tripId: string;
    busId?: string;
    recipients: Array<'office' | 'rep' | 'driver' | 'custom'>;
    customPhone?: string;
    message?: string;
  }): Promise<any> {
    const response = await this.api.post('/housing/report/send', {
      tripId: payload.tripId,
      ...(payload.busId ? { busId: payload.busId } : {}),
      recipients: payload.recipients,
      ...(payload.customPhone?.trim()
        ? { customPhone: payload.customPhone.trim() }
        : {}),
      ...(payload.message?.trim() ? { message: payload.message.trim() } : {}),
    });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService; 